import queryString from 'query-string';
import get from 'lodash/get';

import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import fetchQuery from 'rebook/apps/utils/fetchQuery';
import {
  CATALOG_SERIES_DETAIL_SET_DATA,
  CATALOG_SERIES_DETAIL_RESET_DATA,
} from 'rebook/apps/catalog/constants';
import { Author } from 'rebook/apps/catalog/models';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';


export function fetchInitialData(match, location = {}) {
  return async(dispatch, getState) => {
    const { ssr, seriesDetail: currentState } = getState();
    const pageNumber = parseInt(get(ssr, 'query.page', 1), 10);
    const { s } = queryString.parse(location.search);
    const subscriptionId = get(ssr, 'query.s', null) || s;
    const bookType = get(ssr, 'query.t', null);

    const { seriesSlug } = match.params;

    let newState = {
      seriesSlug,
      subscriptionId,
      pageNumber,
      bookType,
    };
    // obtain partial state from router (client side only)
    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }
    if (currentState.series) {
      newState = {
        ...newState,
        ...currentState,
      };
    }
    dispatch(setData(newState));

    if (!newState.series || newState.series.author === undefined) {
      await dispatch(fetchDetail(match));
    }
    if (!newState.books || currentState.pageNumber !== pageNumber) {
      await dispatch(fetchBookList(newState));
    }
  };
}

export function fetchDetail(match) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const { seriesSlug } = match.params;
    const url = `/api/catalog/series/s-${seriesSlug}/`;
    return (
      fetchQuery(url, { apiVersion: 5, headers: ssr.headers })
        .then(
          ({ data }) => {
            dispatch(setData({ series: data }));
          }
        )
        .catch(
          ({ data, resp, error }) => {
            if (resp.status === 404) {
              dispatch(Error404Actions.show());
            }
            else {
              Raven.captureMessage('failed to load series detail due to server error', {
                level: 'error',
                extra: {
                  status: resp.status,
                  response: data,
                  error,
                },
              });
              throw new Error('failed to load series detail due to server error');
            }
          }
        )
    );
  };
}

export function fetchBookList(params={}) {
  return async(dispatch, getState) => {
    const { ssr, seriesDetail } = getState();
    const mergedState = { ...seriesDetail, ...params };
    const { series, paginateBy, subscriptionId, bookType } = mergedState;

    if (!series) {
      return null;
    }

    let pageNumber = mergedState.pageNumber || 1;
    if (!params.pageNumber &&
      (seriesDetail.subscriptionId !== mergedState.subscriptionId)
      ||(seriesDetail.bookType !== mergedState.bookType)
    ) {
      pageNumber = 1;
    }
    let searchParameterName = 'subscription_id';

    if (subscriptionId && subscriptionId.length > 1) {
      searchParameterName = 'subscription_id__in';
    }

    let queryParams = {
      limit: paginateBy,
      ...(subscriptionId !== null && { [searchParameterName]: subscriptionId }),
      ...(pageNumber > 1 && { offset: (pageNumber - 1) * paginateBy }),
      ...(bookType!==null && { type: bookType }),
    };

    const qs = queryString.stringify(queryParams, { encode: false });
    const url = `/api/catalog/series/${series.id}/books/?${qs}`;
    // clear the book list just before the fetch
    dispatch(setData({
      books: null,
      // optimistically update the UI
      pageNumber,
      bookListFetched: false,
      bookType: null,
    }));
    return (
      fetchQuery(url, { apiVersion: 5, headers: ssr.headers })
        .then(
          ({ data }) => {
            const lastPageNumber = Math.ceil(data.meta.total_count / paginateBy);
            dispatch(setData({
              books: data.objects,
              lastPageNumber: lastPageNumber,
              bookListFetched: true,
              subscriptionId,
              bookType,
            }));
          }
        )
        .catch(
          ({ resp, data, error }) => {
            Raven.captureMessage('failed to load series books due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            throw new Error('failed to load series books due to server error');
          }
        )
    );
  };
}

export function setSeoData() {
  return (dispatch, getState) => {
    const { seriesDetail } = getState();
    const { series, pageNumber, books } = seriesDetail;
    const seriesBookNames = books
      ? books.slice(0, 3).map((seriesBook) => seriesBook.book.name)
      : [];
    const seriesAuthor = series.author ? new Author(series.author) : null;
    const title = `${series.name} — читайте и слушайте онлайн книги` +
                  `${seriesAuthor ? ` ${seriesAuthor.genitive_full_name || seriesAuthor.cover_name}` : ''}` +
                  ` в электронной библиотеке MyBook${pageNumber > 1 ? `, страница ${pageNumber}` : ''}`;
    const description = `Читайте онлайн, слушайте аудиокниги из серии «${series.name}»` +
                        `${seriesAuthor ? `, автор ${seriesAuthor.cover_name}` : ''}. ` +
                        `${seriesBookNames.length > 0 ? `Электронные книги ${seriesBookNames.join(', ')} -` : ''} ` +
                        `отзывы и рейтинг книг в электронной библиотеке MyBook.`;
    const canonicalUrl = `https://${BASE_HOST}${series.absolute_url}`;
    const noRobots = pageNumber > 1;
    dispatch(SeoActions.setData({
      robots: noRobots ? 'noindex, nofollow' : null,
      title,
      description,
      canonicalUrl,
    }));
  };
}

export function setData(payload) {
  return (dispatch) => {
    const { series, books } = payload;
    dispatch({
      type: CATALOG_SERIES_DETAIL_SET_DATA,
      payload,
    });
    if (series || books) {
      dispatch(setSeoData());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch({
      type: CATALOG_SERIES_DETAIL_RESET_DATA,
    });
    dispatch(SeoActions.resetData());
  };
}
